import TicketSaleCardHeader from 'app/components/TicketSaleCard/TicketSaleCardHeader';
import TicketSalePageHeader from 'app/components/TicketSalePageHeader/TicketSalePageHeader';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isRenderedInsideEmbed } from 'utils/isRenderedInsideEmbed';
import {
  API_REQUEST_STATUS_FAIL,
  API_REQUEST_STATUS_NOT_SENT,
  API_REQUEST_STATUS_SUCCESS,
} from 'utils/static/constants';
import ApplyPrivateInvitation from './ApplyPrivateInvitation';
import CardHeaderSkeleton from './CardHeaderSkeleton';
import FreeEventRegistrationFinalMessage from './FreeEventRegistrationFinalMessage';
import FreeEventRegistrationForm from './FreeEventRegistrationForm';
import FreeEventRegistrationPageSEO from './FreeEventRegistrationPageSEO';
import FreeEventRegistrationPageSkeleton from './FreeEventRegistrationPageSkeleton';
import FreeEventRegistrationTicketTypes from './FreeEventRegistrationTicketTypes';
import useFreeEventData from './useFreeEventData';

const FreeEventRegistrationPage = () => {
  const { t } = useTranslation();
  const {
    data: {
      isLoading,
      hasOrderablePrivateTicketTypes,
      numberOfPublicTicketTypes,
      ticketTypes,
      registrationform,
      event: {
        name,
        location,
        dateTimeFormat,
        timeInterval,
        checkoutConfiguration,
      },
    },
    setData,
  } = useFreeEventData();
  const [requestStatus, setRequestStatus] = useState(
    API_REQUEST_STATUS_NOT_SENT,
  );
  const [selectedTicketType, setSelectedTicketType] = useState(0);
  const [formHeight, setFormHeight] = useState(0);
  const formRef = useRef<HTMLDivElement>(null);

  // useEmbedResize([isLoading, selectedTicketType]);

  const updateFormHeight = () => {
    if (formRef?.current) {
      setFormHeight(formRef.current.clientHeight);
    }
  };

  useEffect(() => {
    updateFormHeight();
  }, [isLoading, selectedTicketType]);

  const formIsSubmitted =
    requestStatus === API_REQUEST_STATUS_SUCCESS ||
    requestStatus === API_REQUEST_STATUS_FAIL;

  const selectedTicketTypesIsWhitelisted =
    ticketTypes.length > 0
      ? ticketTypes[selectedTicketType].isWhitelisted
      : false;

  return (
    <div
      className={`page-container ${isRenderedInsideEmbed ? '' : 'mb-[24px]'}`}
    >
      <FreeEventRegistrationPageSEO
        isLoading={isLoading}
        name={name}
        startsAt={timeInterval.startsAt}
        dateTimeFormat={dateTimeFormat}
      />
      <TicketSalePageHeader className="ml-4 mt-4 lg:absolute" />
      <div className="section-container">
        <div
          className={`w-full mx-auto ${
            isRenderedInsideEmbed
              ? ''
              : 'border shadow-md rounded-lg overflow-hidden'
          } md:max-w-[656px] h-full flex flex-col`}
        >
          {isLoading ? (
            <CardHeaderSkeleton />
          ) : (
            <TicketSaleCardHeader
              name={name}
              location={location}
              dateTimeFormat={dateTimeFormat}
              timeInterval={timeInterval}
            />
          )}
          <div
            className="relative p-[16px] sm:p-[24px]"
            style={{
              minHeight: formIsSubmitted ? 128 : formHeight + 48,
              transition: formIsSubmitted ? 'min-height 0.5s linear' : '',
            }}
          >
            <div
              className={`h-[calc(100%-32px)] sm:h-[calc(100%-48px)] ${
                formIsSubmitted
                  ? 'opacity-100 z-100'
                  : 'absolute opacity-0 z-[-1]'
              }  flex justify-center items-center transition-opacity delay-300 duration-500 flex flex-col justify-center items-center gap-[16px]`}
            >
              <FreeEventRegistrationFinalMessage
                requestStatus={requestStatus}
                isWhitelisted={selectedTicketTypesIsWhitelisted}
                confirmationText={
                  checkoutConfiguration.orderConfirmationPageText
                }
              />
            </div>
            <div
              style={{
                display: formIsSubmitted ? 'none' : 'block',
              }}
              ref={formRef}
            >
              {isLoading ? (
                <FreeEventRegistrationPageSkeleton />
              ) : (
                <>
                  {hasOrderablePrivateTicketTypes ? (
                    <div className="mb-4">
                      <ApplyPrivateInvitation
                        numberOfPublicTicketTypes={numberOfPublicTicketTypes}
                        setData={setData}
                      />
                    </div>
                  ) : null}
                  {ticketTypes.length > 1 ? (
                    <FreeEventRegistrationTicketTypes
                      ticketTypes={ticketTypes}
                      selectedTicketType={selectedTicketType}
                      setSelectedTicketType={setSelectedTicketType}
                    />
                  ) : null}
                  {hasOrderablePrivateTicketTypes || ticketTypes.length > 1 ? (
                    <p className="title-sm mb-4">
                      {t('freeEventRegistrationPage.aboutYou')}
                    </p>
                  ) : null}
                  {!hasOrderablePrivateTicketTypes &&
                  ticketTypes.length === 0 ? (
                    <p className="title-sm">
                      {t('freeEventRegistrationPage.registrationEnded')}
                    </p>
                  ) : (
                    <FreeEventRegistrationForm
                      registrationform={registrationform}
                      requestStatus={requestStatus}
                      setRequestStatus={setRequestStatus}
                      selectedTicketTypesIsWhitelisted={
                        selectedTicketTypesIsWhitelisted
                      }
                      ticketTypes={ticketTypes}
                      selectedTicketType={selectedTicketType}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FreeEventRegistrationPage;
